import { useGetProcessQuery } from "@/services/backend/processes/process/service";
import ConfirmationDialog from "@/shared/components/domain/confirmation/confirmation-dialog";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  useErrArtefactNotReady,
  useSetErrArtefactNotReadyFromRtk,
} from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { Button } from "@/shared/components/ui/button";
import { ReactElement, useEffect } from "react";
import { v4 } from "uuid";
import { useCreateCountingMutation } from "@/services/backend/countings/service";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useNavigate } from "react-router";

interface CreateCountingButtonProps {
  processId: string;
  customerId: string;
  documentationId: string;
  disabled?: boolean;
}

export default function CreateCountingButton({
  processId,
  customerId,
  documentationId,
  disabled,
}: CreateCountingButtonProps): ReactElement {
  const nav = useNavigate();
  const { user } = useAuth();
  const [createCounting, { data, error, reset, isLoading, isSuccess }] =
    useCreateCountingMutation();
  const { resetNotReadyError } = useErrArtefactNotReady();
  useSetErrArtefactNotReadyFromRtk(error, () => {
    reset();
  });

  useEffect(() => {
    if (isSuccess && data != null && data.counting != null) {
      nav(
        `/processes/${processId}/counting/${data.counting.id}/first-counting`,
      );
    }
  }, [isSuccess, data, nav, processId]);

  const {
    data: process,
    isLoading: processIsLoading,
    error: processError,
  } = useGetProcessQuery({
    id: processId ?? "",
  });

  if (processIsLoading) {
    return <div />;
  }

  if (processError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    if (!process) {
      return;
    }

    createCounting({
      id: v4(),
      customerId,
      processId,
      documentationId,
      createdBy: user!.id,
    });

    resetNotReadyError();
  };

  return (
    <ConfirmationDialog
      trigger={
        <Button disabled={disabled}>
          <p>Inkubation starten</p>
        </Button>
      }
      title="Inkubation erstellen"
      description="Hiermit wird die Inkubation gestartet."
      onConfirm={onConfirm}
    />
  );
}
